import React, { useState, useRef, useEffect } from 'react';

const VoiceTypeSlider = ({ 
  value = 'bass', 
  onChange, 
  disabled = false,
  className = '' 
}) => {
  // Define voice types from Bass to Soprano (ordered as requested)
  // Map 'alto' to display as 'Contralto' as requested by user
  const voiceTypes = [
    { value: 'bass', label: 'Bass' },
    { value: 'baritone', label: 'Baritone' },
    { value: 'tenor', label: 'Tenor' },
    { value: 'alto', label: 'Contralto' },
    { value: 'mezzo_soprano', label: 'Mezzo Soprano' },
    { value: 'soprano', label: 'Soprano' }
  ];

  const [currentIndex, setCurrentIndex] = useState(
    voiceTypes?.findIndex(type => type?.value === value) || 0
  );
  const [isDragging, setIsDragging] = useState(false);
  
  const sliderRef = useRef(null);
  const thumbRef = useRef(null);

  // Update index when value prop changes
  useEffect(() => {
    const newIndex = voiceTypes?.findIndex(type => type?.value === value);
    if (newIndex !== -1) {
      setCurrentIndex(newIndex);
    }
  }, [value]);

  // Notify parent of changes
  useEffect(() => {
    onChange?.(voiceTypes?.[currentIndex]?.value);
  }, [currentIndex, onChange]);

  const getPercentage = (index) => {
    return (index / (voiceTypes?.length - 1)) * 100;
  };

  const handleMouseDown = (e) => {
    if (disabled) return;
    e?.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || disabled || !sliderRef?.current) return;
    
    e?.preventDefault();
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e?.clientX - rect?.left) / rect?.width) * 100));
    const newIndex = Math.round((percentage / 100) * (voiceTypes?.length - 1));
    
    setCurrentIndex(newIndex);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const handleTrackClick = (e) => {
    if (disabled || isDragging) return;
    
    const rect = sliderRef?.current?.getBoundingClientRect();
    const percentage = ((e?.clientX - rect?.left) / rect?.width) * 100;
    const clickIndex = Math.round((percentage / 100) * (voiceTypes?.length - 1));
    
    setCurrentIndex(clickIndex);
  };

  return (
    <div className={`voice-type-slider ${className}`}>
      {/* Current Selection Display */}
      <div className="mb-3">
        <span className="text-sm font-medium text-blue-100">
          {voiceTypes?.[currentIndex]?.label}
        </span>
      </div>
      
      {/* Slider Track */}
      <div 
        ref={sliderRef}
        className="relative h-6 bg-white bg-opacity-20 rounded-lg cursor-pointer"
        onClick={handleTrackClick}
      >
        {/* Progress Track */}
        <div
          className="absolute h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg"
          style={{
            width: `${getPercentage(currentIndex)}%`
          }}
        />
        
        {/* Thumb - Fixed alignment to center properly */}
        <div
          ref={thumbRef}
          className={`absolute w-6 h-6 bg-white rounded-full shadow-lg cursor-grab transform -translate-y-0 -translate-x-3 border-2 border-blue-500 ${isDragging ? 'cursor-grabbing scale-110' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          style={{ left: `${getPercentage(currentIndex)}%` }}
          onMouseDown={handleMouseDown}
        />
      </div>
      
      {/* Voice Type Labels - Updated to match new order and align with centers */}
      <div className="flex justify-between mt-2 text-xs text-blue-200 px-3">
        <span className="text-center">Bass</span>
        <span className="text-center">Baritone</span>
        <span className="text-center">Tenor</span>
        <span className="text-center">Contralto</span>
        <span className="text-center">Mezzo Soprano</span>
        <span className="text-center">Soprano</span>
      </div>
    </div>
  );
};

export default VoiceTypeSlider;